import { Armchair } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { cn } from '@/utils/cn'
import { seatsText } from '../labels'
import type { SeatSummary } from '../types'

/** Occupancy of next year's sections: enrolled + confirmed (solid) and planned (light). */
export function SeatSummaryPanel({ seats }: { seats: SeatSummary[] }) {
  const { t } = useTranslation('promotion')
  const used = seats.filter((s) => s.enrolled + s.planned + s.confirmed > 0)
  return (
    <Card>
      <CardHeader
        icon={<Armchair />}
        title={t('Seats next year')}
        description={t('Enrolled and confirmed, then planned, against the capacity of each section.')}
      />
      <CardBody>
        {used.length === 0 ? (
          <p className="text-sm text-muted">{t('No placement into a section yet.')}</p>
        ) : (
          <ul className="space-y-3">
            {used.map((s) => {
              const taken = s.enrolled + s.confirmed
              const cap = s.capacity ?? Math.max(taken + s.planned, 1)
              const over = s.capacity !== null && taken + s.planned > s.capacity
              return (
                <li key={s.sectionId}>
                  <div className="flex items-baseline justify-between gap-2 text-sm">
                    <span className="font-medium text-ink">{s.label}</span>
                    <span className={cn('text-xs', over ? 'font-medium text-danger-700' : 'text-muted')}>
                      {seatsText(t, s)}
                    </span>
                  </div>
                  <div
                    className="mt-1 flex h-2 overflow-hidden rounded-full bg-surface-sunken"
                    aria-hidden="true"
                  >
                    <span
                      className="bg-brand-600"
                      style={{ width: `${Math.min(100, (taken / cap) * 100)}%` }}
                    />
                    <span
                      className={over ? 'bg-danger-500/60' : 'bg-brand-300'}
                      style={{ width: `${Math.min(100, (s.planned / cap) * 100)}%` }}
                    />
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </CardBody>
    </Card>
  )
}
